export interface FileReportErrorItem {
  error_description?: string;
  section_field_name?: string;
  status?: string;
  remarks?: string;
  severity?: string;
}

export const SUMMARY_METRIC_ROWS: Array<{ key: string; label: string }> = [
  { key: "files_verified", label: "Files Verified" },
  { key: "total_runs", label: "Total Runs" },
  { key: "completed_runs", label: "Completed Runs" },
  { key: "failed_runs", label: "Failed Runs" },
  { key: "total_errors_found", label: "Total Errors Found" },
  { key: "open_errors", label: "Open Errors" },
  { key: "total_tokens", label: "Total Tokens" },
  { key: "avg_response_time_ms", label: "Avg Response Time Ms" },
];

/** Timestamp for export filenames: yyyy-mm-dd HH:mm */
export function formatReportExportTimestamp(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d} ${h}:${min}`;
}

export function buildReportExportFilename(prefix: string, extension: string): string {
  return `${prefix}_${formatReportExportTimestamp()}.${extension}`;
}

export const FILE_VERIFICATION_HEADERS = [
  "File ID",
  "File Name",
  "Process Type",
  "Run Status",
  "Tokens",
  "Total Errors",
  "Completed Errors",
  "Not Completed Errors",
  "Error Details (with Completion Status)",
  "Run At",
] as const;

function formatErrorCompletionLabel(item: FileReportErrorItem): string {
  if (item.status === "closed") {
    const remark = (item.remarks || "").trim();
    if (remark === "Corrected" || remark === "No Need") {
      return remark;
    }
    return "Completed";
  }
  return "Not Completed";
}

function formatSingleErrorWithStatus(e: FileReportErrorItem, index: number): string {
  const section = (e.section_field_name || "").trim();
  const desc = (e.error_description || "—").trim();
  const prefix = section ? `${section}: ` : "";
  const status = formatErrorCompletionLabel(e);
  return `${index + 1}. ${prefix}${desc} - (${status})`;
}

/** One string per error line (for UI list rendering). */
export function getFileErrorStatusLines(items: FileReportErrorItem[] | undefined): string[] {
  if (!items || !items.length) {
    return [];
  }
  return items.map((e, i) => formatSingleErrorWithStatus(e, i));
}

/** Error detail lines for a summary run row; shows failure reason when run failed. */
export function getRunErrorDetailLines(row: Record<string, unknown>): string[] {
  const errorLines = getFileErrorStatusLines(
    (row.error_items as FileReportErrorItem[]) || [],
  );
  if (errorLines.length) {
    return errorLines;
  }

  const runStatus = String(row.run_status ?? row.last_run_status ?? "").toLowerCase();
  if (runStatus !== "failed") {
    return [];
  }

  const errorMessage = String(row.error_message ?? "").trim();
  if (errorMessage) {
    return [`Run failed: ${errorMessage}`];
  }
  return ["Run failed: No failure reason recorded"];
}

/** Single column text with newline separators (for CSV/Excel export). */
export function formatFileErrorsWithStatus(items: FileReportErrorItem[] | undefined): string {
  const lines = getFileErrorStatusLines(items);
  if (!lines.length) {
    return "—";
  }
  return lines.join("\n");
}

/** Error details text for a summary run row (includes failure reason). */
export function formatRunErrorDetails(row: Record<string, unknown>): string {
  const lines = getRunErrorDetailLines(row);
  if (!lines.length) {
    return "—";
  }
  return lines.join("\n");
}

export function getSummaryMetricRows(
  summary: Record<string, unknown> | null | undefined,
): Array<[string, unknown]> {
  if (!summary) return [];
  return SUMMARY_METRIC_ROWS.map(({ key, label }) => [label, summary[key] ?? 0]);
}

export function getFileVerificationExportRow(
  file: Record<string, unknown>,
): (string | number)[] {
  const notCompleted = Number(file.not_completed_errors_count ?? file.open_errors ?? 0);
  const completed =
    file.completed_errors_count != null
      ? Number(file.completed_errors_count)
      : Math.max(0, Number(file.total_errors || 0) - notCompleted);
  const errorsText = formatRunErrorDetails(file);

  return [
    String(file.file_idfk ?? ""),
    String(file.file_name ?? ""),
    String(file.process_type ?? file.last_process_type ?? ""),
    String(file.run_status ?? file.last_run_status ?? ""),
    Number(file.total_tokens ?? 0),
    Number(file.total_errors ?? 0),
    completed,
    notCompleted,
    errorsText === "—" ? "" : errorsText,
    String(file.last_run_at ?? ""),
  ];
}

/** @deprecated Use formatFileErrorsWithStatus */
export function formatFileErrorReportColumns(items: FileReportErrorItem[] | undefined): {
  errorDetails: string;
  completionStatus: string;
} {
  const combined = formatFileErrorsWithStatus(items);
  if (combined === "—") {
    return { errorDetails: "—", completionStatus: "—" };
  }
  return { errorDetails: combined, completionStatus: "" };
}

/** Report/API/sample error row (allows fields used in sample data and backend). */
export interface FileReportErrorInput extends FileReportErrorItem {
  file_idfk?: string;
  id?: number;
  file_name?: string;
  page_number?: string;
  line_number?: string;
  suggested_correction?: string;
  process_type?: string;
  created_at?: string;
  closed_at?: string | null;
  closed_by_name?: string | null;
}

export function attachErrorsToFileDetails<T extends { file_idfk?: string }>(
  fileDetails: T[],
  allErrors: FileReportErrorInput[],
): Array<T & { error_items: FileReportErrorItem[] }> {
  return fileDetails.map((file) => ({
    ...file,
    error_items: allErrors
      .filter((e) => e.file_idfk === file.file_idfk)
      .map((e) => ({
        error_description: e.error_description,
        section_field_name: e.section_field_name,
        status: e.status,
        remarks: e.remarks,
        severity: e.severity,
      })),
  }));
}
